import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { commercialFeeAuthoringApi, isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { UseQueryResult } from "@tanstack/react-query";
import type {
  CommercialFeePolicyDraft,
  CommercialFeePolicyWrite,
} from "@bw/shared/types/generated";
import { PlatformPage } from "@/components/PlatformPage";
import { FeePolicyEditor } from "../components/FeePolicyEditor";
import {
  useFeePolicyDraft,
  useCreateFeePolicyDraft,
  useReplaceFeePolicyDraft,
} from "../hooks/useFeePolicies";

function draftInputs(
  draft: CommercialFeePolicyDraft,
): CommercialFeePolicyWrite {
  return {
    audience_org_id: draft.audience_org_id,
    effective_from: draft.effective_from,
    effective_until: draft.effective_until,
    rules: draft.rules,
  };
}

function DraftForm({
  initial,
  draftId,
  reload,
  onAuthorityDenied,
}: {
  initial: CommercialFeePolicyDraft | null;
  draftId?: string;
  reload: () => Promise<CommercialFeePolicyDraft | null>;
  onAuthorityDenied: () => void;
}) {
  const [captured, setCaptured] = useState(initial);
  const [value, setValue] = useState<CommercialFeePolicyWrite | null>(() =>
    initial ? draftInputs(initial) : null,
  );
  const [editorVersion, setEditorVersion] = useState(0);
  const [isReloading, setIsReloading] = useState(false);
  const create = useCreateFeePolicyDraft(),
    replace = useReplaceFeePolicyDraft(draftId);
  const navigate = useNavigate();
  const mutation = captured ? replace : create;
  const stale = isApiError(mutation.error) && mutation.error.status === 409;
  const denied = isApiError(mutation.error) && mutation.error.status === 403;
  useEffect(() => {
    if (denied) onAuthorityDenied();
  }, [denied, onAuthorityDenied]);
  function adoptDraft(draft: CommercialFeePolicyDraft) {
    if (draftId && draft.draft_id !== draftId) return;
    setCaptured(draft);
    setValue(draftInputs(draft));
    setEditorVersion((version) => version + 1);
  }
  async function reloadDraft() {
    if (isReloading || mutation.isPending) return;
    setIsReloading(true);
    try {
      const reviewed = await reload();
      if (reviewed && reviewed.draft_id === draftId) {
        adoptDraft(reviewed);
        mutation.reset();
      }
    } finally {
      setIsReloading(false);
    }
  }
  function save() {
    if (!value || mutation.isPending || isReloading || stale || denied) return;
    if (captured && draftId)
      replace.mutate(
        { expected_revision: captured.revision, data: value },
        { onSuccess: adoptDraft },
      );
    else
      create.mutate(value, {
        onSuccess: (result) =>
          navigate(`/contracts/fee-policies/${result.draft_id}`),
      });
  }
  if (denied)
    return (
      <Alert severity="warning">Current Admin fee draft access unavailable.</Alert>
    );
  return (
    <Stack spacing={2}>
      {captured ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="body2">Revision {captured.revision}</Typography>
          <Button size="small" component={Link} to={`/contracts/fee-policies/${captured.draft_id}/publication`}>
            Review publication
          </Button>
        </Stack>
      ) : null}
      {stale ? (
        <Alert severity="warning">
          Draft changed. Reload and review before saving again.{" "}
          <Button
            size="small"
            disabled={isReloading || mutation.isPending}
            onClick={() => void reloadDraft()}
          >
            Reload draft
          </Button>
        </Alert>
      ) : null}
      <FeePolicyEditor
        key={editorVersion}
        value={value}
        onChange={setValue}
        onSave={save}
        isPending={mutation.isPending || isReloading || stale}
        error={
          mutation.isError
            ? "Could not save the draft. Review the source and retry."
            : null
        }
      />
    </Stack>
  );
}

function DraftAccessBoundary({
  draftId,
  query,
}: {
  draftId: string | undefined;
  query: UseQueryResult<CommercialFeePolicyDraft, Error>;
}) {
  const [authorityDenied, setAuthorityDenied] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [reviewedDraft, setReviewedDraft] = useState<CommercialFeePolicyDraft | null>(null);
  const currentDraft =
    query.data && query.data.draft_id === draftId ? query.data : null;
  const [hasOwnedDraft, setHasOwnedDraft] = useState(Boolean(currentDraft));
  const denied =
    query.isError && isApiError(query.error) && query.error.status === 403;
  const latchDenial = useCallback(() => setAuthorityDenied(true), []);
  useEffect(() => {
    if (denied) latchDenial();
  }, [denied, latchDenial]);
  useEffect(() => {
    if (currentDraft) setHasOwnedDraft(true);
  }, [currentDraft]);
  if (denied || authorityDenied)
    return (
      <Alert severity="warning">
        Current Admin fee draft access unavailable. Review current authority
        before continuing.{" "}
        <Button
          size="small"
          disabled={reviewing}
          onClick={async () => {
            setReviewing(true);
            try {
              if (draftId) {
                const result = await query.refetch();
                if (result.isSuccess && result.data.draft_id === draftId) {
                  // Refetch settles before the query observer necessarily paints.
                  // Seed the newly revealed controller from this exact reviewed
                  // result, never a stale/wrong-ID cache snapshot or blank form.
                  setReviewedDraft(result.data);
                  setHasOwnedDraft(true);
                  setAuthorityDenied(false);
                }
              } else {
                // No draft exists yet. Use the real current-Admin catalogue
                // read, never fabricate a draft or automatically create one.
                await commercialFeeAuthoringApi.listDrafts();
                setAuthorityDenied(false);
              }
            } catch {
              // Source failure or wrong-resource data cannot restore authority.
            } finally {
              setReviewing(false);
            }
          }}
        >
          Review current access
        </Button>
      </Alert>
    );
  return (
    <Stack spacing={2}>
      {draftId && query.isError ? (
        <Alert severity="warning">
          Current draft unavailable.{" "}
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {!draftId || currentDraft || hasOwnedDraft ? (
        <DraftForm
          initial={reviewedDraft ?? currentDraft}
          draftId={draftId}
          onAuthorityDenied={latchDenial}
          reload={async () => {
            const result = await query.refetch();
            return result.isSuccess && result.data.draft_id === draftId
              ? result.data
              : null;
          }}
        />
      ) : null}
    </Stack>
  );
}

export function FeePolicyDraftEditorPage() {
  const { user } = useAuth();
  const { draftId } = useParams<{ draftId: string }>();
  const query = useFeePolicyDraft(draftId);
  return (
    <PlatformPage
      title={draftId ? "Edit fee draft" : "New fee draft"}
      isLoading={Boolean(draftId && query.isLoading)}
      actions={
        <Button component={Link} to="/contracts/fee-policies" size="small">
          Back to fee drafts
        </Button>
      }
    >
      <Stack spacing={2}>
        <Alert severity="info">
          Saving a draft does not publish it. Publication review uses the saved revision.
        </Alert>
        <DraftAccessBoundary
          key={`${user?.uid ?? "anonymous"}:${draftId ?? "new"}`}
          draftId={draftId}
          query={query}
        />
      </Stack>
    </PlatformPage>
  );
}
